import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useEmailDomainSuggest } from '../hooks/useEmailDomainSuggest';
import type { ChangeEvent, InputHTMLAttributes, KeyboardEvent } from 'react';

interface InputEmailProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  onValueChange?: (value: string) => void;
}

export const InputEmail = forwardRef<HTMLInputElement, InputEmailProps>(
  (
    {
      className,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      onValueChange,
      ...props
    },
    ref,
  ) => {
    const { getSuggestions } = useEmailDomainSuggest();
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLUListElement>(null);
    const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      return () => {
        if (blurTimeoutRef.current !== null) {
          clearTimeout(blurTimeoutRef.current);
        }
      };
    }, []);

    // 外部からのrefに内部のinputRefを公開
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const updateSuggestions = useCallback(
      (inputValue: string) => {
        const newSuggestions = getSuggestions(inputValue);
        setSuggestions(newSuggestions);
        setShowSuggestions(newSuggestions.length > 0);
        setSelectedIndex(-1);
      },
      [getSuggestions],
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      updateSuggestions(newValue);
      onChange?.(e);
      onValueChange?.(newValue);
    };

    const handleSuggestionClick = (suggestion: string) => {
      setSuggestions([]);
      setShowSuggestions(false);

      // react-hook-form用にchangeイベントをシミュレート
      const input = inputRef.current;
      if (input) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        )?.set;
        nativeInputValueSetter?.call(input, suggestion);

        // inputイベントとchangeイベントの両方をディスパッチ
        const inputEvent = new Event('input', { bubbles: true });
        input.dispatchEvent(inputEvent);

        const changeEvent = new Event('change', { bubbles: true });
        input.dispatchEvent(changeEvent);

        input.focus();
      }

      onValueChange?.(suggestion);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (showSuggestions && suggestions.length > 0) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setSelectedIndex((prev) =>
              prev < suggestions.length - 1 ? prev + 1 : prev,
            );
            return;
          case 'ArrowUp':
            e.preventDefault();
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
            return;
          case 'Enter':
            if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
              e.preventDefault();
              handleSuggestionClick(suggestions[selectedIndex]);
              return;
            }
            break;
          case 'Escape':
            setShowSuggestions(false);
            setSelectedIndex(-1);
            return;
          case 'Tab':
            if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
              e.preventDefault();
              handleSuggestionClick(suggestions[selectedIndex]);
              return;
            } else if (suggestions.length > 0) {
              e.preventDefault();
              handleSuggestionClick(suggestions[0]);
              return;
            }
            break;
        }
      }
      onKeyDown?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // クリックイベントが先に処理されるよう、少し遅延させる
      blurTimeoutRef.current = setTimeout(() => {
        setShowSuggestions(false);
      }, 150);
      onBlur?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (suggestions.length > 0) {
        setShowSuggestions(true);
      }
      onFocus?.(e);
    };

    // 選択中のサジェストが見えるようにスクロール
    useEffect(() => {
      if (selectedIndex >= 0 && suggestionsRef.current) {
        const selectedElement = suggestionsRef.current.children[
          selectedIndex
        ] as HTMLElement;
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest' });
        }
      }
    }, [selectedIndex]);

    return (
      <div className="input-email" style={{ position: 'relative' }}>
        <input
          {...props}
          ref={inputRef}
          type="email"
          className={className}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          autoComplete="off"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls="email-suggestions"
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul
            id="email-suggestions"
            ref={suggestionsRef}
            role="listbox"
            className="input-email__suggestions"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              maxHeight: '200px',
              maxWidth: '300px',
              overflowY: 'auto',
              margin: 0,
              padding: 0,
              listStyle: 'none',
            }}
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion}
                role="option"
                aria-selected={index === selectedIndex}
                onClick={() => handleSuggestionClick(suggestion)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor:
                    index === selectedIndex ? '#f0f0f0' : 'transparent',
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {suggestion}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
);
